import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTheme } from '../theme';
import { useIsMobile } from '../useMediaQuery';
import ProtocolIcon from './ProtocolIcon';
import { RISK_MAP } from '../data';
import { fmt } from '../utils';
import TokenIcon from './TokenIcon';

const MAX_POOLS = 3;
const MIN_POOLS = 2;

const RISK_VALUE = { LOW: 1, MED: 2, HIGH: 3 };

function PoolSelector({ pools, selected, onSelect, onRemove, colors, isMobile }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = useMemo(() => {
    const selectedIds = new Set(selected.map(p => p.id));
    if (!query) return pools.filter(p => !selectedIds.has(p.id)).slice(0, 50);
    const q = query.toLowerCase();
    return pools
      .filter(p => !selectedIds.has(p.id))
      .filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.tokens.some(t => t.toLowerCase().includes(q)) ||
        p.protocol.toLowerCase().includes(q) ||
        p.chain.toLowerCase().includes(q)
      )
      .slice(0, 50);
  }, [pools, query, selected]);

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 10,
        color: colors.labelLight,
        letterSpacing: 1.5,
        marginBottom: 10,
      }}>
        SELECT {MIN_POOLS}-{MAX_POOLS} POOLS TO COMPARE
      </div>

      {/* Selected pills */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        {selected.map(pool => (
          <div key={pool.id} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: colors.surfaceAlt,
            border: `1px solid ${colors.border}`,
            borderRadius: 20,
            padding: '6px 12px 6px 8px',
          }}>
            <div style={{ display: 'flex', gap: -4 }}>
              {pool.tokens.slice(0, 2).map(t => (
                <TokenIcon key={t} symbol={t} size={18} />
              ))}
            </div>
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              color: colors.textPrimary,
              fontWeight: 500,
            }}>
              {pool.name}
            </span>
            <button
              onClick={() => onRemove(pool.id)}
              style={{
                background: 'none',
                border: 'none',
                color: colors.labelMuted,
                cursor: 'pointer',
                fontSize: 14,
                padding: '0 2px',
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Search dropdown */}
      {selected.length < MAX_POOLS && (
        <div ref={ref} style={{ position: 'relative', maxWidth: isMobile ? '100%' : 420 }}>
          <input
            type="text"
            placeholder="Search by name, token, protocol, or chain..."
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            style={{
              width: '100%',
              padding: '10px 14px',
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              color: colors.textPrimary,
              background: colors.inputBg,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          {open && filtered.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              maxHeight: 280,
              overflowY: 'auto',
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              marginTop: 4,
              zIndex: 20,
              boxShadow: `0 8px 24px ${colors.shadow}`,
            }}>
              {filtered.map(pool => (
                <button
                  key={pool.id}
                  onClick={() => {
                    onSelect(pool);
                    setQuery('');
                    setOpen(false);
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 14px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: `1px solid ${colors.borderSubtle}`,
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = colors.hover}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ display: 'flex', gap: 2 }}>
                    {pool.tokens.slice(0, 2).map(t => (
                      <TokenIcon key={t} symbol={t} size={20} />
                    ))}
                  </div>
                  <div>
                    <div style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 12,
                      color: colors.textPrimary,
                      fontWeight: 500,
                    }}>
                      {pool.name}
                    </div>
                    <div style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 10,
                      color: colors.labelMuted,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 3,
                    }}>
                      <ProtocolIcon protocol={pool.protocol} size={10} /> {pool.protocol} · {pool.chain} · {(pool.apr || 0).toFixed(2)}% APR
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BarChart({ values, labels, poolColors, unit, colors, higherIsBetter = true }) {
  const max = Math.max(...values.filter(v => v != null && !isNaN(v)), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {values.map((val, i) => {
        const pct = max > 0 ? (val / max) * 100 : 0;
        const isWinner = higherIsBetter
          ? val === Math.max(...values)
          : val === Math.min(...values);
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10,
              color: colors.labelMuted,
              width: 80,
              textAlign: 'right',
              flexShrink: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {labels[i]}
            </span>
            <div style={{
              flex: 1,
              height: 18,
              background: colors.scoreTrack,
              borderRadius: 4,
              overflow: 'hidden',
              position: 'relative',
            }}>
              <div style={{
                width: `${Math.max(pct, 2)}%`,
                height: '100%',
                background: poolColors[i],
                borderRadius: 4,
                opacity: isWinner ? 1 : 0.5,
                transition: 'width 0.4s ease',
              }} />
            </div>
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              fontWeight: isWinner ? 700 : 400,
              color: isWinner ? colors.textPrimary : colors.loserText,
              width: 80,
              flexShrink: 0,
            }}>
              {unit === '$' ? fmt(val) : unit === '%' ? `${(val || 0).toFixed(2)}%` : val}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function MetricRow({ label, values, poolColors, colors, unit, higherIsBetter = true }) {
  const best = higherIsBetter
    ? Math.max(...values)
    : Math.min(...values);

  return (
    <div style={{
      padding: '16px 0',
      borderBottom: `1px solid ${colors.borderSubtle}`,
    }}>
      <div style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 10,
        color: colors.labelLight,
        letterSpacing: 1.2,
        marginBottom: 10,
      }}>
        {label}
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        {values.map((val, i) => {
          const isWinner = val === best;
          const formatted = unit === '$' ? fmt(val)
            : unit === '%' ? `${(val || 0).toFixed(2)}%`
            : val;
          return (
            <div key={i} style={{
              flex: 1,
              textAlign: 'center',
              padding: '10px 8px',
              borderRadius: 8,
              background: isWinner ? colors.greenBg : 'transparent',
              border: isWinner ? `1px solid ${colors.greenBorder}` : `1px solid transparent`,
              transition: 'all 0.2s ease',
            }}>
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 16,
                fontWeight: 600,
                color: isWinner ? colors.green : colors.textSecondary,
              }}>
                {formatted}
              </div>
              {isWinner && (
                <div style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 8,
                  color: colors.green,
                  letterSpacing: 1,
                  marginTop: 4,
                }}>
                  BEST
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RiskRow({ label, pools, colors }) {
  const values = pools.map(p => RISK_VALUE[p.risk] || 2);
  const bestVal = Math.min(...values);

  return (
    <div style={{
      padding: '16px 0',
      borderBottom: `1px solid ${colors.borderSubtle}`,
    }}>
      <div style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 10,
        color: colors.labelLight,
        letterSpacing: 1.2,
        marginBottom: 10,
      }}>
        {label}
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        {pools.map((pool, i) => {
          const risk = RISK_MAP[pool.risk] || RISK_MAP.MED;
          const isWinner = values[i] === bestVal;
          return (
            <div key={i} style={{
              flex: 1,
              textAlign: 'center',
              padding: '10px 8px',
              borderRadius: 8,
              background: isWinner ? colors.greenBg : 'transparent',
              border: isWinner ? `1px solid ${colors.greenBorder}` : `1px solid transparent`,
            }}>
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11,
                fontWeight: 600,
                color: risk.color,
                background: risk.bg,
                border: `1px solid ${risk.border}`,
                padding: '3px 10px',
                borderRadius: 4,
              }}>
                {pool.risk}
              </span>
              {isWinner && (
                <div style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 8,
                  color: colors.green,
                  letterSpacing: 1,
                  marginTop: 6,
                }}>
                  SAFEST
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TextRow({ label, pools, getter, colors }) {
  return (
    <div style={{
      padding: '16px 0',
      borderBottom: `1px solid ${colors.borderSubtle}`,
    }}>
      <div style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 10,
        color: colors.labelLight,
        letterSpacing: 1.2,
        marginBottom: 10,
      }}>
        {label}
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        {pools.map((pool, i) => (
          <div key={i} style={{
            flex: 1,
            textAlign: 'center',
            padding: '10px 8px',
          }}>
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              color: colors.textSecondary,
              fontWeight: 500,
            }}>
              {getter(pool)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ILRiskRow({ pools, colors }) {
  const getIL = (pool) => {
    if (pool.stablecoin) return 'None';
    const riskVal = RISK_VALUE[pool.risk] || 2;
    if (riskVal === 1) return 'Low';
    if (riskVal === 2) return 'Medium';
    return 'High';
  };
  const ilValue = { 'None': 0, 'Low': 1, 'Medium': 2, 'High': 3 };
  const values = pools.map(p => ilValue[getIL(p)]);
  const bestVal = Math.min(...values);

  return (
    <div style={{
      padding: '16px 0',
      borderBottom: `1px solid ${colors.borderSubtle}`,
    }}>
      <div style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 10,
        color: colors.labelLight,
        letterSpacing: 1.2,
        marginBottom: 10,
      }}>
        IL RISK
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        {pools.map((pool, i) => {
          const il = getIL(pool);
          const isWinner = values[i] === bestVal;
          const ilColor = il === 'None' ? colors.green
            : il === 'Low' ? colors.green
            : il === 'Medium' ? colors.gold
            : colors.red;
          return (
            <div key={i} style={{
              flex: 1,
              textAlign: 'center',
              padding: '10px 8px',
              borderRadius: 8,
              background: isWinner ? colors.greenBg : 'transparent',
              border: isWinner ? `1px solid ${colors.greenBorder}` : `1px solid transparent`,
            }}>
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 12,
                fontWeight: 600,
                color: ilColor,
              }}>
                {il}
              </span>
              {isWinner && (
                <div style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 8,
                  color: colors.green,
                  letterSpacing: 1,
                  marginTop: 6,
                }}>
                  LOWEST
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VerdictSection({ pools, colors }) {
  if (pools.length === 0) return null;
  // Yield seeker: highest APR
  const yieldWinner = pools.reduce((best, p) => p.apr > best.apr ? p : best, pools[0]);
  // Safety first: lowest risk, then highest TVL
  const safetyWinner = pools.reduce((best, p) => {
    const pRisk = RISK_VALUE[p.risk] || 2;
    const bRisk = RISK_VALUE[best.risk] || 2;
    if (pRisk < bRisk) return p;
    if (pRisk === bRisk && p.tvl > best.tvl) return p;
    return best;
  }, pools[0]);
  // High volume: highest volume
  const volumeWinner = pools.reduce((best, p) => (p.volume24h || 0) > (best.volume24h || 0) ? p : best, pools[0]);

  const verdicts = [
    {
      label: 'YIELD SEEKERS',
      icon: '\u{1F525}',
      pool: yieldWinner,
      reason: `Highest APR at ${(yieldWinner.apr || 0).toFixed(2)}%`,
      accent: colors.gold,
      accentBg: colors.goldBg,
      accentBorder: colors.goldBorder,
    },
    {
      label: 'SAFETY FIRST',
      icon: '\u{1F6E1}',
      pool: safetyWinner,
      reason: `${safetyWinner.risk} risk with ${fmt(safetyWinner.tvl)} TVL`,
      accent: colors.green,
      accentBg: colors.greenBg,
      accentBorder: colors.greenBorder,
    },
    {
      label: 'HIGH VOLUME',
      icon: '\u{1F4C8}',
      pool: volumeWinner,
      reason: `${fmt(volumeWinner.volume24h || 0)} daily volume`,
      accent: '#5470D4',
      accentBg: colors.surfaceAlt,
      accentBorder: colors.border,
    },
  ];

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{
        fontFamily: "'DM Serif Display', serif",
        fontSize: 22,
        color: colors.textPrimary,
        marginBottom: 20,
      }}>
        Verdict
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {verdicts.map(v => (
          <div key={v.label} style={{
            flex: '1 1 240px',
            background: v.accentBg,
            border: `1px solid ${v.accentBorder}`,
            borderRadius: 12,
            padding: '20px',
          }}>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10,
              color: v.accent,
              letterSpacing: 1.5,
              marginBottom: 12,
              fontWeight: 600,
            }}>
              {v.icon} {v.label}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 2 }}>
                {v.pool.tokens.slice(0, 2).map(t => (
                  <TokenIcon key={t} symbol={t} size={20} />
                ))}
              </div>
              <span style={{
                fontFamily: "'DM Serif Display', serif",
                fontSize: 16,
                color: colors.textPrimary,
              }}>
                {v.pool.name}
              </span>
            </div>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              color: colors.textSecondary,
            }}>
              {v.reason}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const POOL_COLORS = ['#5470D4', '#D4A72C', '#E0006E'];

export default function ComparatorView({ pools }) {
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  const [selected, setSelected] = useState([]);

  const handleSelect = (pool) => {
    if (selected.length >= MAX_POOLS) return;
    if (selected.find(p => p.id === pool.id)) return;
    setSelected(prev => [...prev, pool]);
  };

  const handleRemove = (id) => {
    setSelected(prev => prev.filter(p => p.id !== id));
  };

  const ready = selected.length >= MIN_POOLS;

  return (
    <div style={{ padding: isMobile ? '16px' : '24px', maxWidth: 960, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: 28,
          color: colors.textPrimary,
          marginBottom: 6,
        }}>
          Pool Comparator
        </div>
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 12,
          color: colors.labelMuted,
        }}>
          Compare pools side by side to find the best fit for your strategy.
        </div>
      </div>

      {/* Pool selector */}
      <PoolSelector
        pools={pools}
        selected={selected}
        onSelect={handleSelect}
        onRemove={handleRemove}
        colors={colors}
        isMobile={isMobile}
      />

      {!ready && (
        <div style={{
          textAlign: 'center',
          padding: '60px 0',
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 12,
          color: colors.labelMuted,
        }}>
          Select at least {MIN_POOLS} pools to start comparing.
        </div>
      )}

      {ready && (
        <>
          {/* Pool headers */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 16,
            marginBottom: 8,
            padding: '16px 0',
            borderBottom: `2px solid ${colors.border}`,
          }}>
            {selected.map((pool, i) => (
              <div key={pool.id} style={{
                flex: 1,
                textAlign: 'center',
              }}>
                <div style={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  background: POOL_COLORS[i],
                  margin: '0 auto 8px',
                }} />
                <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginBottom: 6 }}>
                  {pool.tokens.slice(0, 3).map(t => (
                    <TokenIcon key={t} symbol={t} size={22} />
                  ))}
                </div>
                <div style={{
                  fontFamily: "'DM Serif Display', serif",
                  fontSize: 15,
                  color: colors.textPrimary,
                  marginBottom: 4,
                }}>
                  {pool.name}
                </div>
                <div style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 10,
                  color: colors.labelMuted,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 3,
                }}>
                  <ProtocolIcon protocol={pool.protocol} size={10} /> {pool.protocol} · {pool.chain}
                </div>
              </div>
            ))}
          </div>

          {/* Comparison metrics */}
          <MetricRow
            label="TOTAL APR"
            values={selected.map(p => p.apr || 0)}
            poolColors={selected.map((_, i) => POOL_COLORS[i])}
            colors={colors}
            unit="%"
            higherIsBetter
          />
          <MetricRow
            label="BASE APY"
            values={selected.map(p => p.apyBase || 0)}
            poolColors={selected.map((_, i) => POOL_COLORS[i])}
            colors={colors}
            unit="%"
            higherIsBetter
          />
          <MetricRow
            label="REWARD APY"
            values={selected.map(p => p.apyReward || 0)}
            poolColors={selected.map((_, i) => POOL_COLORS[i])}
            colors={colors}
            unit="%"
            higherIsBetter
          />
          <MetricRow
            label="TVL"
            values={selected.map(p => p.tvl)}
            poolColors={selected.map((_, i) => POOL_COLORS[i])}
            colors={colors}
            unit="$"
            higherIsBetter
          />
          <MetricRow
            label="VOLUME 24H"
            values={selected.map(p => p.volume24h || 0)}
            poolColors={selected.map((_, i) => POOL_COLORS[i])}
            colors={colors}
            unit="$"
            higherIsBetter
          />
          <MetricRow
            label="FEES 24H"
            values={selected.map(p => p.fees24h || 0)}
            poolColors={selected.map((_, i) => POOL_COLORS[i])}
            colors={colors}
            unit="$"
            higherIsBetter
          />
          <RiskRow label="RISK LEVEL" pools={selected} colors={colors} />
          <TextRow
            label="FEE TIER"
            pools={selected}
            getter={p => p.fee || 'N/A'}
            colors={colors}
          />
          <ILRiskRow pools={selected} colors={colors} />

          {/* Visual bar charts */}
          <div style={{ marginTop: 32 }}>
            <div style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: 22,
              color: colors.textPrimary,
              marginBottom: 20,
            }}>
              Visual Comparison
            </div>

            {[
              { label: 'APR', key: 'apr', unit: '%', higher: true },
              { label: 'TVL', key: 'tvl', unit: '$', higher: true },
              { label: 'Volume 24h', key: 'volume24h', unit: '$', higher: true },
              { label: 'Fees 24h', key: 'fees24h', unit: '$', higher: true },
            ].map(metric => (
              <div key={metric.key} style={{ marginBottom: 20 }}>
                <div style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 10,
                  color: colors.labelLight,
                  letterSpacing: 1.2,
                  marginBottom: 8,
                }}>
                  {metric.label.toUpperCase()}
                </div>
                <BarChart
                  values={selected.map(p => p[metric.key] || 0)}
                  labels={selected.map(p => p.name.length > 12 ? p.name.slice(0, 12) + '..' : p.name)}
                  poolColors={selected.map((_, i) => POOL_COLORS[i])}
                  unit={metric.unit}
                  colors={colors}
                  higherIsBetter={metric.higher}
                />
              </div>
            ))}
          </div>

          {/* Verdict */}
          <VerdictSection pools={selected} colors={colors} />

          {/* Bottom spacer */}
          <div style={{ height: 60 }} />
        </>
      )}
    </div>
  );
}
